import React, { useEffect, useState } from 'react';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import toast, { Toaster } from 'react-hot-toast';
import { useSelector } from 'react-redux';
import { useAppDispatch } from '../../app/hooks';
import { RootState } from '../../app/store';
import Button from '../../components/button/Button';
import { fetchUserDetails, setUserStatus } from '../../features/authSlice';
import {
  GetBankInfo,
  UpdateBankInfo,
  UpdateBrandInfo,
} from '../../features/partnerSlice';
import RedStar from '../RedStar';
import AppPreviewPair from '../../components/appPreview/AppPreview';
import { ColorInput } from '../colorPicker/colorPicker';
import { descriptionSuggestions, tagSuggestions } from '../../assets/dummy';
import { BankInfoData, BrandingFormData } from '../../utils/type';
import { FileUpload } from '../FileUpload/fileUpload';
import { BsInfoCircle } from 'react-icons/bs';

import { checkAliasLink, checkB2CLink } from '../../utils/generalFunctions';

export const extractFileName = (url: any) => {
  return url.split('/').pop();
};

export const TextFieldLoader = () => {
  return (
    <div>
      <label className='block text-sm font-medium text-gray-700 mb-4'>
        &nbsp;
      </label>
      <div className='flex items-center w-full space-x-2 relative animate-pulse h-12'>
        <div className='w-full h-10 bg-gray-200 rounded-md' />
      </div>
    </div>
  );
};

const BrandingInformation = () => {
  const [partnerId, setPartnerId] = useState<string | null>(null);
  const [availability, setAvailability] = useState('');
  const [aliasAvailability, setAliasAvailability] = useState('');
  const [preview, setPreview] = useState<string | null>(null);
  const [previewBanner, setPreviewBanner] = useState<string | null>(null);
  const [, setLogoX] = React.useState(10);
  const [activeTooltip, setActiveTooltip] = useState<string | null>(null);

  const userId = useSelector((state: RootState) => state.auth.user?.id);

  //Fetch Brand Info and bank Info
  const BrandInfoState = useSelector(
    (state: RootState) => state.partner.brandInfo
  );
  const loadingBrandInfo = useSelector(
    (state: RootState) => state.partner.loadingStates['GetBrandInfoState']
  );
  const BankInfoState = useSelector(
    (state: RootState) => state.partner.paymentInfo
  );

  const userDetails: any = localStorage.getItem('userDetails');
  const dispatch = useAppDispatch();

  const {
    register,
    handleSubmit,
    setValue,
    getValues,
    clearErrors,
    watch,
    control,
    formState: { errors },
    setError,
  } = useForm<BrandingFormData>({
    mode: 'onChange',
    defaultValues: {
      partnerId,
    },
  });
  const commonFileUploadProps = {
    isLoading: loadingBrandInfo,
    register,
    errors,
    watch,
    setError,
    clearErrors,
    setValue,
    accept: 'image/png, image/jpeg',
    maxFileSizeMB: 2,
  };
  const descValue = watch('description') || '';
  const tagValue = watch('tag') || '';
  const b2cLink = watch('b2cLink') || '';
  const appDeepLink = watch('appDeepLink') || '';

  const normalizeB2cLink = (link: string | undefined): string => {
    if (!link) return '';
    const withoutProtocol = link.replace(/^https?:\/\//, '');
    return withoutProtocol.split('/')[0];
  };

  const ensureDomainWithSuffix = (link: string | undefined): string => {
    const domain = normalizeB2cLink(link);
    if (!domain) return '';
    return domain.includes('.') ? domain : `${domain}.saversapp.com`;
  };

  const formatB2cLinkForSubmission = (prefix: string | undefined): string => {
    if (!prefix) return '';
    const normalizedPrefix = prefix.trim().toLowerCase();
    if (!normalizedPrefix) return '';
    return `${normalizedPrefix}.saversapp.com`;
  };

  const toComparableB2cLink = (link: string | undefined): string => {
    return ensureDomainWithSuffix(link).toLowerCase();
  };

  const storedB2cDomain = ensureDomainWithSuffix(BrandInfoState?.b2cLink);
  const storedB2cHref = storedB2cDomain ? `https://${storedB2cDomain}` : '';
  const storedAppDeepLinkHref = BrandInfoState?.appDeepLink
    ? BrandInfoState.appDeepLink.startsWith('http')
      ? BrandInfoState.appDeepLink
      : `https://${BrandInfoState.appDeepLink}`
    : '';

  // Extract custom part from full b2cLink URL (first part before first dot)
  const extractB2cLink = (fullUrl: string) => {
    if (!fullUrl) return '';
    // Remove https:// or http:// if present
    let cleanLink = fullUrl.replace(/^https?:\/\//, '');
    // Extract the first part before the first dot
    return cleanLink.split('.')[0] || '';
  };

  // Extract custom part from full appDeepLink URL
  const extractAppDeepLink = (fullUrl: string) => {
    if (!fullUrl) return '';
    return fullUrl.replace('https://earncashback.app.link/', '');
  };

  const handleMouseEnter = (tooltipType: string) => {
    setActiveTooltip(tooltipType);
  };

  const handleMouseLeave = () => {
    setActiveTooltip(null);
  };

  useEffect(() => {
    dispatch(GetBankInfo());
  }, [dispatch]);

  useEffect(() => {
    if (BrandInfoState) {
      // Set values for brand info
      setValue('orgDisplayName', BrandInfoState.orgDisplayName);
      setValue('b2cLink', extractB2cLink(BrandInfoState.b2cLink || ''));
      setValue(
        'appDeepLink',
        extractAppDeepLink(BrandInfoState.appDeepLink || '')
      );
      setValue('logo', BrandInfoState.logo || '');
      setValue('logoMobile', BrandInfoState.logoMobile || '');
      setValue('logoMonochrome', BrandInfoState.logoMonochrome || '');
      setValue('icon', BrandInfoState.icon || '');
      setValue('logoNewsletter', BrandInfoState.logoNewsletter || '');
      setValue('tmCertificate', BrandInfoState.tmCertificate || '');
      setValue('additionalDocument', BrandInfoState.additionalDocument || '');
      setValue('logoBanner', BrandInfoState.logoBanner || '');
      setValue('tag', BrandInfoState.tag || '');
      setValue('description', BrandInfoState.description || '');
      setValue('primaryColor', BrandInfoState.primaryColor || undefined);
      setValue('secondaryColor', BrandInfoState.secondaryColor || undefined);
      setValue('tertiaryColor', BrandInfoState.tertiaryColor || undefined);
      setValue('quaternaryColor', BrandInfoState.quaternaryColor || undefined);
      setValue(
        'einNumber',
        BrandInfoState.einNumber === 0 || BrandInfoState.einNumber === 'null'
          ? ''
          : BrandInfoState.einNumber
      );
      setValue('websiteLink', BrandInfoState.websiteLink || '');
      setValue('facebookLink', BrandInfoState.facebookLink || '');
      setValue('instagramLink', BrandInfoState.instagramLink || '');
      setValue('other', BrandInfoState.other || '');

      // Set file upload fields with the filename extracted from URL
    }

    if (BankInfoState) {
      setValue('accountHolderName', BankInfoState.accountHolderName || '');
      setValue('bankName', BankInfoState.bankName || '');
      setValue('routingNumber', BankInfoState.routingNumber || '');
      setValue('accountNumber', BankInfoState.accountNumber || '');
      if (BankInfoState.accountNumber) {
        setValue('reEnterAccountNumber', BankInfoState.accountNumber || '');
      }
      setValue('swiftCode', BankInfoState.swiftCode || '');
      setValue('email', BankInfoState.email || '');
      setValue('phone', BankInfoState.phone || '');
      setValue('tin', BankInfoState.tin || '');
      setValue('iban', BankInfoState.iban || '');
    }
  }, [BrandInfoState, BankInfoState, setValue]);

  useEffect(() => {
    if (userDetails) {
      const userObject = JSON.parse(userDetails);
      setPartnerId(userObject.partnerId);
    }
  }, [userDetails]);

  const handleSaveDraft = async () => {
    const formData = getValues();
    // Clear required fields errors
    const requiredFields = Object.entries(errors)
      .filter(([_, value]) => value?.type === 'required')
      .map(([key]) => key);

    if (requiredFields.length > 0) {
      clearErrors(requiredFields as any);
    }
    // Scroll to first error field if there are any errors
    if (errors && Object.keys(errors).length > 0) {
      const firstErrorField = Object.keys(errors)[0];

      const errorElement = document.querySelector(
        `[name="${firstErrorField}"]`
      ) as HTMLElement;

      if (errorElement) {
        errorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        errorElement.focus();
      }
      // Save form data if there are no errors (except required fields)
    } else {
      SaveForm(formData);
    }
  };

  const handleSaveSubmit = async () => {
    await new Promise(resolve => {
      setTimeout(resolve, 0);
    });

    handleSubmit(onSubmit)();
  };

  const SaveForm = async (data: BrandingFormData) => {
    try {
      if (!partnerId) return;

      const brandFormData = new FormData();
      brandFormData.append('partnerId', partnerId);

      // Handle regular text fields (excluding b2cLink and appDeepLink)
      const textFields: (keyof BrandingFormData)[] = [
        'orgDisplayName',
        'tag',
        'description',
        'einNumber',
        'primaryColor',
        'secondaryColor',
        'tertiaryColor',
        'quaternaryColor',
        'websiteLink',
        'facebookLink',
        'instagramLink',
        'other',
      ];

      textFields.forEach(field => {
        if (data[field] !== BrandInfoState?.[field]) {
          brandFormData.append(field, String(data[field] || ''));
        }
      });

      // Handle b2cLink - send with full URL
      const submittedB2cLink = formatB2cLinkForSubmission(data.b2cLink);
      if (
        submittedB2cLink.toLowerCase() !==
        toComparableB2cLink(BrandInfoState?.b2cLink)
      ) {
        brandFormData.append('b2cLink', submittedB2cLink);
      }

      // Handle appDeepLink - send with full URL
      if (data.appDeepLink !== BrandInfoState?.appDeepLink) {
        const fullAppDeepLink = data.appDeepLink
          ? `https://earncashback.app.link/${data.appDeepLink}`
          : '';
        brandFormData.append('appDeepLink', fullAppDeepLink);
      }
      const fileFields: (keyof BrandingFormData)[] = [
        'logo',
        'logoMobile',
        'logoMonochrome',
        'logoBanner',
        'additionalDocument',
        'tmCertificate',
        'icon',
        'logoNewsletter',
      ];

      fileFields.forEach(field => {
        const file = data?.[field]?.[0];
        if (file instanceof File) {
          brandFormData.append(field, file);
        }
      });
      let isBrandFormEmpty = true;

      for (let [key] of Array.from(brandFormData.entries())) {
        if (key !== 'partnerId') {
          isBrandFormEmpty = false;
          break;
        }
      }

      const bankFormData = new FormData();
      bankFormData.append('partnerId', partnerId);
      const bankTextFields: (keyof Partial<BankInfoData>)[] = [
        'accountHolderName',
        'bankName',
        'routingNumber',
        'accountNumber',
        'swiftCode',
        'email',
        'phone',
        'tin',
        'iban',
      ];
      bankTextFields.forEach(field => {
        if (data[field] !== BankInfoState?.[field]) {
          bankFormData.append(field, String(data[field] || ''));
        }
      });

      let isBankFormEmpty = true;

      for (let [key] of Array.from(bankFormData.entries())) {
        if (key !== 'partnerId') {
          isBankFormEmpty = false;
          break;
        }
      }
      if (!isBrandFormEmpty) {
        const result: any = await dispatch(UpdateBrandInfo(brandFormData));
        if (result?.status === 200) {
          toast.success('Draft saved successfully');
        } else {
          toast.error('Failed to update brand information. Please try again.');
        }
      }
      if (!isBankFormEmpty) dispatch(UpdateBankInfo(partnerId, bankFormData));
      if (!isBrandFormEmpty || !isBankFormEmpty)
        dispatch(fetchUserDetails(userId as any));
      if (isBrandFormEmpty && isBankFormEmpty)
        toast.error('No changes to save');
    } catch (error) {
      console.error(error);
    }
  };

  const onSubmit: SubmitHandler<BrandingFormData> = async data => {
    try {
      if (!partnerId) return;

      const brandFormData = new FormData();
      brandFormData.append('partnerId', partnerId);
      brandFormData.append('brandInfo', 'completed');

      // Handle regular text fields (excluding b2cLink and appDeepLink)
      const textFields: (keyof BrandingFormData)[] = [
        'orgDisplayName',
        'tag',
        'description',
        'einNumber',
        'primaryColor',
        'secondaryColor',
        'tertiaryColor',
        'quaternaryColor',
        'websiteLink',
        'facebookLink',
        'instagramLink',
        'other',
      ];

      textFields.forEach(field => {
        if (data[field] !== BrandInfoState?.[field]) {
          brandFormData.append(field, String(data[field] || ''));
        }
      });

      // Handle b2cLink - send with full URL
      const submittedB2cLink = formatB2cLinkForSubmission(data.b2cLink);
      if (
        submittedB2cLink.toLowerCase() !==
        toComparableB2cLink(BrandInfoState?.b2cLink)
      ) {
        brandFormData.append('b2cLink', submittedB2cLink);
      }

      // Handle appDeepLink - send with full URL
      if (data.appDeepLink !== BrandInfoState?.appDeepLink) {
        const fullAppDeepLink = data.appDeepLink
          ? `https://earncashback.app.link/${data.appDeepLink}`
          : '';
        brandFormData.append('appDeepLink', fullAppDeepLink);
      }
      const fileFields: (keyof BrandingFormData)[] = [
        'logo',
        'logoMobile',
        'logoMonochrome',
        'logoBanner',
        'additionalDocument',
        'tmCertificate',
        'icon',
        'logoNewsletter',
      ];

      fileFields.forEach(field => {
        const file = data?.[field]?.[0];
        if (file instanceof File) {
          brandFormData.append(field, file);
        }
      });

      const bankFormData = new FormData();
      bankFormData.append('partnerId', partnerId);
      const bankTextFields: (keyof Partial<BankInfoData>)[] = [
        'accountHolderName',
        'bankName',
        'routingNumber',
        'accountNumber',
        'swiftCode',
        'email',
        'phone',
        'tin',
        'iban',
      ];
      bankTextFields.forEach(field => {
        if (data[field] !== BankInfoState?.[field]) {
          bankFormData.append(field, String(data[field] || ''));
        }
      });

      const updateBrand: any = await dispatch(UpdateBrandInfo(brandFormData));
      if (updateBrand.status === 200) {
        dispatch(fetchUserDetails(userId as any));
        dispatch(setUserStatus('inreview'));
      }
      dispatch(UpdateBankInfo(partnerId, bankFormData));

      // toast.success('Form submitted successfully');
    } catch (error) {
      console.error(error);
      toast.error('Error submitting form. Please try again later.');
    }
  };

  useEffect(() => {
    if (!preview && !BrandInfoState?.logo) return;
    const img = new Image();
    img.onload = () => {
      if (img.width / img.height < 2 && img.width / img.height > 0.5) {
        setLogoX(-10);
      } else {
        setLogoX(10);
      }
    };
    img.src = preview || BrandInfoState?.logo;
  }, [preview, BrandInfoState?.logo]);

  const b2cHasLocalError = !!(
    errors.b2cLink &&
    errors.b2cLink.type !== 'manual' &&
    !!errors.b2cLink.message
  );
  const aliasHasLocalError = !!(
    errors.appDeepLink &&
    errors.appDeepLink.type !== 'manual' &&
    !!errors.appDeepLink.message
  );
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!b2cLink || b2cHasLocalError) {
      setAvailability('');
      return;
    }

    const controller = new AbortController();
    const timeout = setTimeout(async () => {
      setAvailability('checking');
      try {
        const available = await checkB2CLink(controller, b2cLink);
        if (available) {
          setAvailability('available');
          clearErrors('b2cLink');
        } else {
          setAvailability('taken');
          setError('b2cLink', {
            type: 'manual',
            message: 'B2C link is already taken',
          });
        }
      } catch {
        setAvailability('');
      }
    }, 1500);

    return () => {
      clearTimeout(timeout);
      controller.abort();
    };
  }, [b2cLink, b2cHasLocalError, clearErrors, setError]);

  useEffect(() => {
    if (!appDeepLink || aliasHasLocalError) {
      setAliasAvailability('');
      return;
    }

    const controller = new AbortController();
    const timeout = setTimeout(async () => {
      setAliasAvailability('checking');
      try {
        const available = await checkAliasLink(controller, appDeepLink);
        if (available) {
          setAliasAvailability('available');
          clearErrors('appDeepLink');
        } else {
          setAliasAvailability('taken');
          setError('appDeepLink', {
            type: 'manual',
            message: 'Deep link alias is already taken',
          });
        }
      } catch {
        setAliasAvailability('');
      }
    }, 1500);

    return () => {
      clearTimeout(timeout);
      controller.abort();
    };
  }, [appDeepLink, aliasHasLocalError, clearErrors, setError]);

  return (
    <div>
      <>
        <h2 className=' mt-[-8rem] mb-[7rem] text-[1.5rem] block md:hidden'>
          Program Information
        </h2>
        <h4 className='mt-[-6rem] mb-8 md:text-left font-normal text-[#808080] leading-8'>
          To complete your registration, please fill in the sections below and
          upload the required logos and documents. If you don't have all the
          information at this time, you can press 'Save' to continue later and
          submit when completed.
        </h4>
      </>
      <Toaster position='top-center' />
      <form onSubmit={handleSubmit(onSubmit)}>
        {/* Name Field */}

        <div className=' justify-center max-w-[full] mx-auto primary-shadow rounded-lg'>
          <div className='bg-white primary-shadow rounded-lg w-full'>
            <div className='mb-8'>
              <div className='mb-6 pb-4'>
                <h2 className='text-white bg-primary p-4 px-8 rounded-t-lg text-lg font-semibold'>
                  Branding
                </h2>
                <div className='mb-0 mt-4 px-4 md:px-8 md:py-4'>
                  <p className='text-base mb-8 text-[#343434] '>
                    Enter your organization's name to displayed on the app and
                    website. Upload a high-resolution logo in either JPEG or PNG
                    format. Specify your brand's primary and secondary colors
                    using hex codes.
                  </p>
                  <div className='grid lg:grid-cols-2 gap-x-8 gap-y-4 pb-4 mb-6'>
                    {loadingBrandInfo ? (
                      <TextFieldLoader />
                    ) : (
                      <div>
                        <label className='block text-[#000000] text-sm font-[500] mb-2'>
                          Organization Display Name <RedStar />
                        </label>
                        <input
                          type='text'
                          id='orgDisplayName'
                          className='register-input w-full'
                          {...register('orgDisplayName', {
                            required: 'Organization display name is required',
                          })}
                        />

                        <p className='text-[0.75rem] font-semibold text-[#d90000] mt-[5px]'>
                          {errors.orgDisplayName?.message}
                        </p>
                      </div>
                    )}
                    {loadingBrandInfo ? (
                      <TextFieldLoader />
                    ) : (
                      <div>
                        <div className='flex justify-between items-center mb-2'>
                          <label className='block text-[#000000] text-sm font-[500]'>
                            Program URL <RedStar />
                          </label>
                          <div
                            className='relative group inline-block'
                            onMouseEnter={() => handleMouseEnter('programUrl')}
                            onMouseLeave={handleMouseLeave}
                          >
                            <BsInfoCircle
                              color='#1133BB'
                              size={18}
                              className='pointer-events-auto cursor-pointer'
                            />
                            {activeTooltip === 'programUrl' && (
                              <div className='hidden group-hover:block absolute top-full right-0 mt-2 w-60 bg-white border border-gray-300 rounded-md shadow-md z-10 p-2'>
                                <p className='text-sm text-center whitespace-normal'>
                                  This is the website of your Savers App
                                  program. Once the program is live, this can no
                                  longer be changed.
                                </p>
                              </div>
                            )}
                          </div>
                        </div>

                        <div className='flex my-3'>
                          <Controller
                            control={control}
                            name='b2cLink'
                            defaultValue={extractB2cLink(
                              BrandInfoState?.b2cLink || ''
                            )}
                            rules={{
                              required: 'Program URL is required',
                              validate: value => {
                                if (!value) return true;
                                if (!/^[a-z0-9-]+$/.test(value)) {
                                  return 'Only lowercase letters, numbers, and "-" are allowed';
                                }
                                if (availability === 'taken') {
                                  return 'B2C link is already taken';
                                }

                                return true;
                              },
                            }}
                            render={({ field }) => (
                              <input
                                type='text'
                                id='b2cLink'
                                autoComplete='off'
                                placeholder='Your Program URL'
                                className='register-input w-full rounded-r-none my-0'
                                {...field}
                              />
                            )}
                          />
                          <div className='flex items-center px-3 bg-gray-100 border border-l-0 border-gray-300 rounded-r-md text-gray-700 text-sm'>
                            .saversapp.com
                          </div>
                        </div>
                        {storedB2cDomain && (
                          <a
                            href={storedB2cHref}
                            target='_blank'
                            rel='noopener noreferrer'
                            className='text-[0.75rem] font-semibold text-primary mt-[5px] pointer-events-auto inline-block'
                          >
                            {storedB2cDomain}
                          </a>
                        )}

                        <div className='min-h-5'>
                          {availability === 'available' &&
                            b2cLink &&
                            !errors.b2cLink && (
                              <p className='text-[0.75rem] font-semibold text-[#00C389] mt-[5px]'>
                                ✅ Link is available
                              </p>
                            )}
                          {availability === 'checking' && (
                            <p className='text-[0.75rem] font-semibold text-primary mt-[5px]'>
                              Checking availability...
                            </p>
                          )}
                          {errors.b2cLink && (
                            <p className='text-[0.75rem] font-semibold text-[#d90000] mt-[5px]'>
                              {errors.b2cLink.message}
                            </p>
                          )}
                        </div>
                      </div>
                    )}
                    {loadingBrandInfo ? (
                      <TextFieldLoader />
                    ) : (
                      <div>
                        <div className='flex justify-between items-center mb-2'>
                          <label className='block text-[#000000] text-sm font-[500]'>
                            Referral Link <RedStar />
                          </label>
                          <div
                            className='relative group inline-block'
                            onMouseEnter={() =>
                              handleMouseEnter('referralLink')
                            }
                            onMouseLeave={handleMouseLeave}
                          >
                            <BsInfoCircle
                              color='#1133BB'
                              size={18}
                              className='pointer-events-auto cursor-pointer'
                            />
                            {activeTooltip === 'referralLink' && (
                              <div className='hidden group-hover:block absolute top-full right-0 mt-2 w-60 bg-white border border-gray-300 rounded-md shadow-md z-10 p-2'>
                                <p className='text-sm whitespace-normal'>
                                  This alias will be appended to the link that
                                  will be shared for referrals. Once the program
                                  is live, this can no longer be changed.
                                </p>
                              </div>
                            )}
                          </div>
                        </div>

                        <div className='flex my-3'>
                          <div className='flex items-center px-3 bg-gray-100 border border-r-0 border-gray-300 rounded-l-md text-gray-700 text-sm'>
                            https://earncashback.app.link/
                          </div>

                          <Controller
                            control={control}
                            name='appDeepLink'
                            defaultValue={BrandInfoState?.appDeepLink || ''}
                            rules={{
                              required: 'Referral Link is required',
                              validate: value => {
                                if (!value) return true;
                                if (!/^[a-z0-9-]+$/.test(value)) {
                                  return 'Only lowercase letters, numbers, and "-" are allowed';
                                }
                                if (aliasAvailability === 'taken') {
                                  return 'Referral Link is already taken';
                                }
                                return true;
                              },
                            }}
                            render={({ field }) => (
                              <input
                                type='text'
                                id='appDeepLink'
                                autoComplete='off'
                                placeholder='Your Referral Link'
                                className='register-input w-full rounded-l-none my-0'
                                {...field}
                              />
                            )}
                          />
                        </div>
                        {storedAppDeepLinkHref && (
                          <a
                            href={storedAppDeepLinkHref}
                            target='_blank'
                            rel='noopener noreferrer'
                            className='text-[0.75rem] font-semibold text-primary mt-[5px] pointer-events-auto inline-block'
                          >
                            {storedAppDeepLinkHref.replace(/^https?:\/\//, '')}
                          </a>
                        )}

                        <div className='min-h-5'>
                          {aliasAvailability === 'available' &&
                            watch('appDeepLink') &&
                            !errors.appDeepLink && (
                              <p className='text-[0.75rem] font-semibold text-[#00C389] mt-[5px]'>
                                ✅ Link is available
                              </p>
                            )}

                          {aliasAvailability === 'checking' && (
                            <p className='text-[0.75rem] font-semibold text-primary mt-[5px]'>
                              Checking availability...
                            </p>
                          )}

                          {errors.appDeepLink && (
                            <p className='text-[0.75rem] font-semibold text-[#d90000] mt-[5px]'>
                              {errors.appDeepLink.message}
                            </p>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                  <div className='grid lg:grid-cols-2 gap-x-8 gap-y-4 pb-4 mb-6'>
                    <FileUpload
                      {...commonFileUploadProps}
                      name='logo'
                      label='Upload Logo (optimal dimension is 800 * 400)'
                      description="Upload your organization's logo to be featured in the app. For the best results, use a transparent background and remove/crop any blank border around the logo"
                      backendFileName={
                        BrandInfoState?.logo
                          ? extractFileName(BrandInfoState?.logo)
                          : ''
                      }
                      isRequired={true}
                      requiredMessage='Logo is required'
                      backendFile={BrandInfoState?.logo}
                      setPreview={setPreview}
                      onFileReady={file => {
                        const previewURL = URL.createObjectURL(file);
                        setPreview(previewURL);
                      }}
                    />

                    {/* <FileUpload
                      {...commonFileUploadProps}
                      name='logoMobile'
                      label='Upload Mobile Logo (optimal dimension is 800 * 400)'
                      backendFileName={
                        BrandInfoState?.logoMobile
                          ? extractFileName(BrandInfoState?.logoMobile)
                          : ''
                      }
                      description='Optionally, upload a smaller logo or icon for optimal display on mobile screens.'
                    /> */}

                    <FileUpload
                      {...commonFileUploadProps}
                      name='logoMonochrome'
                      label='Upload Monochrome Logo (optimal dimension is 800 * 400)'
                      backendFileName={
                        BrandInfoState?.logoMonochrome
                          ? extractFileName(BrandInfoState?.logoMonochrome)
                          : ''
                      }
                    />
                    <FileUpload
                      {...commonFileUploadProps}
                      name='icon'
                      label='Upload Favicon (optimal dimension is 200 * 200)'
                      dimentions={{ width: 200, height: 200 }}
                      backendFileName={
                        BrandInfoState?.icon
                          ? extractFileName(BrandInfoState?.icon)
                          : ''
                      }
                    />
                    {/* <FileUpload
                      {...commonFileUploadProps}
                      name='logoNewsletter'
                      label='upload Email/Newsletter logo (optimal dimension is 160 * 80)'
                      dimentions={{ width: 160, height: 80 }}
                      backendFileName={
                        BrandInfoState?.logoNewsletter
                          ? extractFileName(BrandInfoState?.logoNewsletter)
                          : ''
                      }
                    /> */}
                    <FileUpload
                      {...commonFileUploadProps}
                      name='logoBanner'
                      label='Upload Banner Image (optimal dimension is 400 * 190)'
                      dimentions={{ width: 400, height: 190 }}
                      backendFileName={
                        BrandInfoState?.logoBanner
                          ? extractFileName(BrandInfoState?.logoBanner)
                          : ''
                      }
                      backendFile={BrandInfoState?.logoBanner}
                      moreDetails='This is OPTIONAL. If uploaded, this banner will be displayed with other banners in the app’s home page carousel. This can be used by the program to promote your brand or products & services. If clicked, the banner will lead to your brand description page in the app.'
                      setPreview={setPreviewBanner}
                      onFileReady={file => {
                        const previewURL = URL.createObjectURL(file);
                        setPreviewBanner(previewURL);
                      }}
                    />
                    {loadingBrandInfo ? (
                      <TextFieldLoader />
                    ) : (
                      <div>
                        <div className='flex justify-between items-center mb-2'>
                          <label className='block text-[#000000] text-sm font-[500]'>
                            Visit Website URL <RedStar />
                          </label>
                          <div
                            className='relative group inline-block'
                            onMouseEnter={() =>
                              handleMouseEnter('visitWebsite')
                            }
                            onMouseLeave={handleMouseLeave}
                          >
                            <BsInfoCircle
                              color='#1133BB'
                              size={18}
                              className='pointer-events-auto cursor-pointer'
                            />
                            {activeTooltip === 'visitWebsite' && (
                              <div className='hidden group-hover:block absolute top-full right-0 mt-2 w-60 bg-white border border-gray-300 rounded-md shadow-md z-10 p-2'>
                                <p className='text-sm whitespace-normal'>
                                  This is an optional URL that can be embedded
                                  in the "Visit Website" button at the bottom of
                                  your brand description page. If not provided,
                                  the Program URL will be embedded instead.
                                </p>
                              </div>
                            )}
                          </div>
                        </div>
                        <input
                          type='text'
                          id='visitWebsiteLink'
                          placeholder='Your Visit Website URL'
                          className='register-input w-full'
                          {...register('visitWebsiteLink', {
                            required: 'Visit Website URL is required',
                          })}
                        />
                        <p className='text-[0.75rem] font-semibold text-[#d90000] mt-[5px]'>
                          {errors.visitWebsiteLink?.message}
                        </p>
                      </div>
                    )}
                    {/* {logoBanner &&
                      (logoBanner.length > 0 || BrandInfoState?.logoBanner) && (
                        <div>
                          {loadingBrandInfo ? (
                            <TextFieldLoader />
                          ) : (
                            <div>
                              <label className='block text-[#000000] text-sm font-[500] mb-2'>
                                Banner URL
                              </label>
                              <input
                                type='text'
                                id='logoBannerUrl'
                                className='register-input w-full'
                                {...register('logoBannerUrl')}
                              />
                              <p className='text-[0.75rem] font-semibold text-[#d90000] mt-[5px]'>
                                {errors.logoBannerUrl?.message}
                              </p>
                            </div>
                          )}
                        </div>
                      )} */}
                  </div>
                </div>
                {/* Row 2 */}
                <div className='grid grid-cols-1 lg:grid-cols-2 gap-8 '>
                  <div>
                    <div className='mb-0 mt-4 px-4 md:px-8 md:py-4'>
                      <div className='grid grid-cols-1 gap-x-8 gap-y-4'>
                        <ColorInput<BrandingFormData>
                          name={'primaryColor' as keyof BrandingFormData}
                          label='Primary Color (Hex)'
                          defaultColor='#1133BB'
                          register={register}
                          isLoading={loadingBrandInfo}
                          description='Used for landing page background and selected
                              icons and headers'
                          watch={watch}
                          setValue={setValue}
                          error={errors.primaryColor?.message}
                        />
                        <ColorInput<BrandingFormData>
                          name={'secondaryColor' as keyof BrandingFormData}
                          label='Secondary Color (Hex) '
                          defaultColor='#00C389'
                          register={register}
                          isLoading={loadingBrandInfo}
                          description='Used for secondary icons and titles'
                          watch={watch}
                          setValue={setValue}
                          error={errors.secondaryColor?.message}
                        />

                        <ColorInput<BrandingFormData>
                          name={'tertiaryColor' as keyof BrandingFormData}
                          label='Third Color (Hex) '
                          defaultColor='#1133BB'
                          register={register}
                          isLoading={loadingBrandInfo}
                          description='Used for category icons and text'
                          watch={watch}
                          setValue={setValue}
                          error={errors.tertiaryColor?.message}
                        />
                        <ColorInput<BrandingFormData>
                          name={'quaternaryColor' as keyof BrandingFormData}
                          label='Fourth Color (Hex) '
                          defaultColor='#1133BB'
                          register={register}
                          isLoading={loadingBrandInfo}
                          description='Used for action buttons'
                          watch={watch}
                          setValue={setValue}
                          error={errors.quaternaryColor?.message}
                        />
                      </div>
                    </div>
                    <div className='mb-0 mt-4 px-4 md:px-8'>
                      <div className='grid grid-cols-1 gap-x-8 gap-y-4 pb-4 mb-6'>
                        {loadingBrandInfo ? (
                          <TextFieldLoader />
                        ) : (
                          <div>
                            <label className='block text-[#000000] text-sm font-[500] mb-2'>
                              Tag Line / Slogan (30 character max) <RedStar />
                            </label>
                            <input
                              type='text'
                              id='tag'
                              autoComplete='off'
                              maxLength={30}
                              className='register-input w-full'
                              {...register('tag', {
                                required: 'Tag is required',
                              })}
                            />
                            {tagValue.length >= 30 ? (
                              <p className='text-[0.75rem] text-red-600 font-medium mt-1'>
                                You’ve reached the maximum allowed characters.
                              </p>
                            ) : (
                              <p className='text-[0.75rem] text-[#878787] '>
                                Add your program’s tag line or slogan, or select
                                one of the provided options.
                              </p>
                            )}

                            <p className='text-[0.75rem] font-semibold text-[#d90000] mt-[5px]'>
                              {errors.tag?.message}
                            </p>
                            {/* Suggested Tags */}
                            <div className='flex flex-wrap gap-2 mt-2'>
                              {tagSuggestions.map(suggestion => (
                                <button
                                  key={suggestion}
                                  type='button'
                                  className='px-3 py-1 bg-gray-200 hover:bg-gray-300 text-sm rounded-lg text-gray-700 border border-gray-300 transition'
                                  onClick={() => setValue('tag', suggestion)}
                                >
                                  {suggestion}
                                </button>
                              ))}
                            </div>

                            {/* Error message */}
                          </div>
                        )}

                        {loadingBrandInfo ? (
                          <TextFieldLoader />
                        ) : (
                          <div>
                            <label className='block text-[#000000] text-sm font-[500] mb-2'>
                              Description (200 characters max) <RedStar />
                            </label>
                            <textarea
                              id='description'
                              className='register-input py-2 w-full'
                              maxLength={200}
                              rows={4.5}
                              autoComplete='off'
                              {...register('description', {
                                required: 'description is required',
                              })}
                            />

                            {descValue.length >= 200 ? (
                              <p className='text-[0.75rem] text-red-600 font-medium mt-1'>
                                You’ve reached the maximum allowed characters.
                              </p>
                            ) : (
                              <p className='text-[0.75rem] text-[#878787] '>
                                Add a description of your program and/or brand
                                for your members to learn more about you , or
                                select one of the provided options.
                              </p>
                            )}

                            <p className='text-[0.75rem] font-semibold text-[#d90000] mt-[5px]'>
                              {errors.description?.message}
                            </p>
                            <div className='flex flex-wrap gap-2 mt-2'>
                              {descriptionSuggestions.map(suggestion => (
                                <button
                                  key={suggestion}
                                  type='button'
                                  className='px-3 py-1 bg-gray-200 hover:bg-gray-300 text-sm rounded-lg text-gray-700 border border-gray-300 text-left'
                                  onClick={() =>
                                    setValue('description', suggestion)
                                  }
                                >
                                  {suggestion}
                                </button>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className='place-items-center'>
                    <div className='grid grid-cols-1 sm:grid-cols-2 gap-10 py-10 pl-5 pr-4 max-w-xl rounded-lg lg:grid-cols-1 xsl:grid-cols-2'>
                      {loadingBrandInfo ? (
                        <AppPreviewPair brand={{}} loading />
                      ) : (
                        <AppPreviewPair
                          brand={{
                            primary:
                              watch('primaryColor') ||
                              BrandInfoState?.primaryColor ||
                              '#1133BB',
                            secondary:
                              watch('secondaryColor') ||
                              BrandInfoState?.secondaryColor ||
                              '#00C389',
                            tertiary:
                              watch('tertiaryColor') ||
                              BrandInfoState?.tertiaryColor ||
                              '#1133BB',
                            quaternary:
                              watch('quaternaryColor') ||
                              BrandInfoState?.quaternaryColor ||
                              '#00C389',
                            logo: preview || BrandInfoState?.logo || '',
                            orgName: watch('orgDisplayName') || '',
                            tagLine: watch('tag') || '',
                            description: watch('description') || '',
                            banner:
                              previewBanner ||
                              BrandInfoState?.logoBanner ||
                              'https://s3.us-east-1.amazonaws.com/assets.savers.club/partner-assets/image.png',
                            partnerId,
                          }}
                        />
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Social Media Info */}

        <div className='justify-center max-w-[full] mx-auto primary-shadow rounded-lg'>
          <div className='bg-white primary-shadow rounded-lg w-full'>
            <div className='mb-8'>
              <div className='mb-6 pb-4'>
                <h2 className='text-white bg-primary p-4 px-8 rounded-t-lg text-lg font-semibold'>
                  Social Media Info
                </h2>
                <div className='mb-0 mt-4 px-4 md:px-8 md:py-4'>
                  <p className='text-base mb-8 text-[#878787]'>
                    Enter your organization's website and social media links
                    here.
                  </p>
                  <p className='text-[0.75rem] font-semibold text-[#d90000] mt-[2px] mb-[5px]'>
                    {errors.socialMedia?.message}
                  </p>
                  <div className='grid md:grid-cols-2 gap-x-8 gap-y-4'>
                    {loadingBrandInfo ? (
                      <TextFieldLoader />
                    ) : (
                      <div>
                        <label className='block text-[#000000] text-sm font-[500] mb-2'>
                          Website <RedStar />
                        </label>
                        <input
                          type='text'
                          id='websiteLink'
                          className='register-input w-full'
                          {...register('websiteLink', {
                            required: 'Website Link is required',
                          })}
                        />
                        <p className='text-[0.75rem] font-semibold text-[#d90000] mt-[5px]'>
                          {errors.websiteLink?.message}
                        </p>
                      </div>
                    )}
                    {loadingBrandInfo ? (
                      <TextFieldLoader />
                    ) : (
                      <div>
                        <label className='block text-[#000000] text-sm font-[500] mb-2'>
                          Facebook
                        </label>
                        <input
                          type='text'
                          id='facebookLink'
                          className='register-input w-full'
                          {...register('facebookLink')}
                        />
                        <p className='text-[0.75rem] font-semibold text-[#d90000] mt-[5px]'>
                          {errors.facebookLink?.message}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
                {/* Row 2 */}
                <div className='mb-0 mt-4 px-4 md:px-8 md:py-4'>
                  <div className='grid md:grid-cols-2 gap-x-8 gap-y-4'>
                    {loadingBrandInfo ? (
                      <TextFieldLoader />
                    ) : (
                      <div>
                        <label className='block text-[#000000] text-sm font-[500] mb-2'>
                          Instagram
                        </label>
                        <input
                          type='text'
                          id='instagramLink'
                          className='register-input w-full'
                          {...register('instagramLink')}
                        />
                        <p className='text-[0.75rem] font-semibold text-[#d90000] mt-[5px]'>
                          {errors.instagramLink?.message}
                        </p>
                      </div>
                    )}
                    {loadingBrandInfo ? (
                      <TextFieldLoader />
                    ) : (
                      <div>
                        <label className='block text-[#000000] text-sm font-[500] mb-2'>
                          Other
                        </label>
                        <input
                          type='text'
                          id='other'
                          className='register-input w-full'
                          {...register('other')}
                        />
                        <p className='text-[0.75rem] font-semibold text-[#d90000] mt-[5px]'>
                          {errors.other?.message}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Payout Account */}

        <div className='justify-center max-w-[full] mx-auto primary-shadow rounded-lg mt-8'>
          <div className='bg-white primary-shadow rounded-lg w-full'>
            <div className='mb-8'>
              <div className='mb-6 pb-4'>
                <h2 className='text-white bg-primary p-4 px-8 rounded-t-lg text-lg font-semibold'>
                  Payout Account
                </h2>
                <div className='mb-0 mt-4 px-4 md:px-8 md:py-4'>
                  <p className='text-base mb-8 text-[#343434]'>
                    Enter your bank account details to receive payouts.
                  </p>
                  <div className='grid md:grid-cols-2 gap-x-8 gap-y-4 py-4'>
                    {loadingBrandInfo ? (
                      <TextFieldLoader />
                    ) : (
                      <div>
                        <label className='block text-[#000000] text-sm font-[500] mb-2'>
                          Account Holder Name <RedStar />
                        </label>
                        <input
                          type='text'
                          id='accountHolderName'
                          className='register-input w-full'
                          {...register('accountHolderName', {
                            required: 'Account Holder Name is required',
                          })}
                        />
                        <p className='text-[0.75rem] font-semibold text-[#d90000] mt-[5px]'>
                          {errors.accountHolderName?.message}
                        </p>
                      </div>
                    )}
                    {loadingBrandInfo ? (
                      <TextFieldLoader />
                    ) : (
                      <div>
                        <label className='block text-[#000000] text-sm font-[500] mb-2'>
                          Sales Tax Number
                        </label>
                        <input
                          type='text'
                          id='tin'
                          className='register-input w-full'
                          {...register('tin')}
                        />
                        <p className='text-[0.75rem] font-semibold text-[#d90000] mt-[5px]'>
                          {errors.tin?.message}
                        </p>
                      </div>
                    )}
                  </div>

                  <div className='grid md:grid-cols-2 gap-x-8 gap-y-4 mt-4 py-4'>
                    {loadingBrandInfo ? (
                      <TextFieldLoader />
                    ) : (
                      <div>
                        <label className='block text-[#000000] text-sm font-[500] mb-2'>
                          Bank Name <RedStar />
                        </label>
                        <input
                          type='text'
                          id='bankName'
                          className='register-input w-full'
                          {...register('bankName', {
                            required: 'Bank Name is required',
                          })}
                        />
                        <p className='text-[0.75rem] font-semibold text-[#d90000] mt-[5px]'>
                          {errors.bankName?.message}
                        </p>
                      </div>
                    )}
                    {loadingBrandInfo ? (
                      <TextFieldLoader />
                    ) : (
                      <div>
                        <label className='block text-[#000000] text-sm font-[500] mb-2'>
                          Routing Number <RedStar />
                        </label>
                        <input
                          type='text'
                          id='routingNumber'
                          className='register-input w-full'
                          {...register('routingNumber', {
                            required: 'Routing Number is required',
                          })}
                        />
                        <p className='text-[0.75rem] font-semibold text-[#d90000] mt-[5px]'>
                          {errors.routingNumber?.message}
                        </p>
                      </div>
                    )}
                  </div>

                  <div className='grid md:grid-cols-2 gap-x-8 gap-y-4 mt-4 py-4'>
                    {loadingBrandInfo ? (
                      <TextFieldLoader />
                    ) : (
                      <div>
                        <label className='block text-[#000000] text-sm font-[500] mb-2'>
                          Account Number <RedStar />
                        </label>
                        <input
                          type='text'
                          id='accountNumber'
                          className='register-input w-full'
                          {...register('accountNumber', {
                            required: 'Account Number is required',
                          })}
                        />
                        <p className='text-[0.75rem] font-semibold text-[#d90000] mt-[5px]'>
                          {errors.accountNumber?.message}
                        </p>
                      </div>
                    )}

                    {loadingBrandInfo ? (
                      <TextFieldLoader />
                    ) : (
                      <div>
                        <label className='block text-[#000000] text-sm font-[500] mb-2'>
                          Re-enter Account Number <RedStar />
                        </label>
                        <input
                          type='text'
                          id='reEnterAccountNumber'
                          className='register-input w-full'
                          {...register('reEnterAccountNumber', {
                            required:
                              'Re-entering your account number is required',
                            validate: value =>
                              value === getValues('accountNumber') ||
                              'Account numbers do not match',
                          })}
                        />
                        <p className='text-[0.75rem] font-semibold text-[#d90000] mt-[5px]'>
                          {errors.reEnterAccountNumber?.message}
                        </p>
                      </div>
                    )}
                  </div>

                  <div className='grid md:grid-cols-2 gap-x-8 gap-y-4 mt-4 py-4'>
                    {loadingBrandInfo ? (
                      <TextFieldLoader />
                    ) : (
                      <div>
                        <label className='block text-[#000000] text-sm font-[500] mb-2'>
                          Swift Code
                        </label>
                        <input
                          type='text'
                          id='swiftCode'
                          className='register-input w-full'
                          {...register('swiftCode')}
                        />
                        <p className='text-[0.75rem] font-semibold text-[#d90000] mt-[5px]'>
                          {errors.swiftCode?.message}
                        </p>
                      </div>
                    )}

                    {loadingBrandInfo ? (
                      <TextFieldLoader />
                    ) : (
                      <div>
                        <label className='block text-[#000000] text-sm font-[500] mb-2'>
                          IBAN
                        </label>
                        <input
                          type='text'
                          id='iban'
                          className='register-input w-full'
                          {...register('iban')}
                        />
                        <p className='text-[0.75rem] font-semibold text-[#d90000] mt-[5px]'>
                          {errors.iban?.message}
                        </p>
                      </div>
                    )}
                  </div>

                  <div className='grid md:grid-cols-2 gap-x-8 gap-y-4 mt-4 py-4'>
                    {loadingBrandInfo ? (
                      <TextFieldLoader />
                    ) : (
                      <div>
                        <label className='block text-[#000000] text-sm font-[500] mb-2'>
                          Registered Email
                        </label>
                        <input
                          type='email'
                          id='email'
                          className='register-input w-full'
                          {...register('email')}
                        />
                        <p className='text-[0.75rem] font-semibold text-[#d90000] mt-[5px]'>
                          {errors.email?.message}
                        </p>
                      </div>
                    )}
                    {loadingBrandInfo ? (
                      <TextFieldLoader />
                    ) : (
                      <div>
                        <label className='block text-[#000000] text-sm font-[500] mb-2'>
                          Registered Contact Number
                        </label>
                        <input
                          type='text'
                          id='phone'
                          className='register-input w-full'
                          {...register('phone', {
                            pattern: {
                              value:
                                /^\+?[0-9]{1,4}[\s-]?\(?[0-9]{1,5}\)?[\s-]?[0-9]{1,9}[\s-]?[0-9]{1,9}$/,
                              message: 'Invalid phone number format',
                            },
                          })}
                        />
                        <p className='text-[0.75rem] font-semibold text-[#d90000] mt-[5px]'>
                          {errors.phone?.message}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* AdditionalInfo */}

        <div className=' justify-center max-w-[full] mx-auto primary-shadow rounded-lg'>
          <div className='bg-white primary-shadow rounded-lg w-full'>
            <div className='mb-8'>
              <div className='mb-6  pb-4'>
                <h2 className='text-white bg-primary p-4 px-8 rounded-t-lg text-lg font-semibold'>
                  Additional Program Info
                </h2>
                <div className='mb-0 mt-4 px-4 md:px-8 md:py-4'>
                  <p className='text-base mb-8 text-[#878787] '>
                    Enter your organization's TIN/EIN (Taxpayer / Employer
                    Identification Number). Upload your company's registration
                    document and trademark certificate, if any. For nonprofit
                    organizations, please ensure that the registration document
                    clearly identifies the company's nonprofit status.
                  </p>

                  {/* row 1 */}
                  <div className='grid lg:grid-cols-2 gap-x-8 gap-y-4'>
                    <FileUpload
                      name='additionalDocument'
                      label='Company Registration Certificate'
                      isRequired={true}
                      requiredMessage='Company Registration Certificate is required'
                      isLoading={loadingBrandInfo}
                      register={register}
                      errors={errors}
                      watch={watch}
                      setError={setError}
                      clearErrors={clearErrors}
                      setValue={setValue}
                      backendFileName={
                        BrandInfoState?.additionalDocument
                          ? extractFileName(BrandInfoState?.additionalDocument)
                          : ''
                      }
                      maxFileSizeMB={2}
                    />

                    {loadingBrandInfo ? (
                      <TextFieldLoader />
                    ) : (
                      <div>
                        <label className='block text-[#000000] text-sm font-[500] mb-2'>
                          TIN/EIN Number <RedStar />
                        </label>
                        <input
                          type='text'
                          id='einNumber'
                          className='register-input w-full'
                          {...register('einNumber', {
                            required: 'TIN/EIN Number is required',
                          })}
                        />
                        <p className='text-[0.75rem] font-semibold text-[#d90000] mt-[5px]'>
                          {errors.einNumber?.message}
                        </p>
                      </div>
                    )}
                  </div>

                  <div className='grid lg:grid-cols-2 gap-x-8 gap-y-4 md:mb-6 mb-4'>
                    <FileUpload
                      name='tmCertificate'
                      label='Trademark Certificate'
                      register={register}
                      errors={errors}
                      isLoading={loadingBrandInfo}
                      watch={watch}
                      setError={setError}
                      clearErrors={clearErrors}
                      setValue={setValue}
                      backendFileName={
                        BrandInfoState?.tmCertificate
                          ? extractFileName(BrandInfoState?.tmCertificate)
                          : ''
                      }
                      maxFileSizeMB={2}
                    />
                  </div>

                  {/* Additional Info - row 2 */}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Submit Button */}
        <div className='w-full flex justify-end gap-4'>
          <Button
            type='button'
            onClick={handleSaveDraft}
            className={`w-full  text-sm px-5 text-white bg-primary border-primary min-w-[7rem] py-2 md:w-[7rem] rounded-[10px] font-semibold`}
          >
            Save Draft
          </Button>

          <Button
            type='button'
            onClick={handleSaveSubmit}
            className={`w-full  text-sm px-5 text-white bg-primary border-primary min-w-[7rem] py-2 md:w-[7rem] rounded-[10px] font-semibold`}
          >
            Submit
          </Button>
        </div>
      </form>
    </div>
  );
};

export default BrandingInformation;
